/**
 * Watched Fields Management Hook
 * Handles field watching for dynamic behavior and data source dependencies
 */
import { useMemo, useRef, useEffect } from 'react'
import { useWatch, Control } from 'react-hook-form'
import { FormFieldConfig, DataSourceConfig } from '../types'
import { getFieldsToWatch } from '../utils/dynamicFields'
import { getDataSourceDependencies } from '../utils/dataSource'

interface UseWatchedFieldsProps {
  fields: FormFieldConfig[]
  dataSources?: DataSourceConfig[]
  linkageRules?: any[]
  control: Control<any>
  onWatchedValuesChange?: (
    currentValues: Record<string, any>,
    prevValues: Record<string, any>
  ) => void
}

interface UseWatchedFieldsReturn {
  fieldsToWatch: string[]
  watchedValues: Record<string, any>
  handleWatchedFieldsChange: (
    currentValues: Record<string, any>,
    prevValues: Record<string, any>
  ) => void
}

export const useWatchedFields = ({
  fields,
  dataSources = [],
  linkageRules = [],
  control,
  onWatchedValuesChange
}: UseWatchedFieldsProps): UseWatchedFieldsReturn => {
  const prevWatchedValuesRef = useRef<Record<string, any>>({})

  /**
   * Determine which fields need to be watched for dynamic behavior
   */
  const fieldsToWatch = useMemo(() => {
    const dynamicFields = getFieldsToWatch(fields)
    const dataSourceDependencies = getDataSourceDependencies(dataSources)
    const linkageTriggerFields = linkageRules.map(rule => rule.triggerField)

    return [
      ...new Set([
        ...dynamicFields,
        ...dataSourceDependencies,
        ...linkageTriggerFields
      ])
    ]
  }, [fields, dataSources, linkageRules])

  // Subscribing via useWatch (not the stable `watch` fn) is what actually
  // triggers re-renders on the watched fields — the previous `watch(names)`
  // wrapped in useMemo returned a frozen snapshot and never re-evaluated.
  const rawWatchedValues = useWatch({
    control,
    name: fieldsToWatch.length > 0 ? fieldsToWatch : []
  }) as any

  const watchedValues = useMemo<Record<string, any>>(() => {
    if (fieldsToWatch.length === 0) return {}
    const arr = Array.isArray(rawWatchedValues)
      ? rawWatchedValues
      : [rawWatchedValues]
    return fieldsToWatch.reduce<Record<string, any>>((acc, field, idx) => {
      acc[field] = arr[idx]
      return acc
    }, {})
  }, [rawWatchedValues, fieldsToWatch])

  /**
   * Handle watched fields change logic
   */
  const handleWatchedFieldsChange = (
    currentValues: Record<string, any>,
    prevValues: Record<string, any>
  ) => {
    const hasChanged = fieldsToWatch.some(field => {
      const currentValue = currentValues[field]
      const prevValue = prevValues[field]

      if (currentValue !== prevValue) {
        return true
      }

      if (Array.isArray(currentValue) && Array.isArray(prevValue)) {
        return JSON.stringify(currentValue) !== JSON.stringify(prevValue)
      }

      if (typeof currentValue === 'object' && typeof prevValue === 'object') {
        return JSON.stringify(currentValue) !== JSON.stringify(prevValue)
      }

      return false
    })

    if (hasChanged && onWatchedValuesChange) {
      onWatchedValuesChange(currentValues, prevValues)
    }
  }

  useEffect(() => {
    const currentWatchedValues = watchedValues
    const prevWatchedValues = prevWatchedValuesRef.current

    if (Object.keys(prevWatchedValues).length === 0) {
      prevWatchedValuesRef.current = { ...currentWatchedValues }
      return
    }

    handleWatchedFieldsChange(currentWatchedValues, prevWatchedValues)

    prevWatchedValuesRef.current = { ...currentWatchedValues }
  }, [watchedValues, onWatchedValuesChange])

  return {
    fieldsToWatch,
    watchedValues,
    handleWatchedFieldsChange
  }
}
